import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Subject } from 'rxjs';

type StorageType = 'local' | 'session';

@Injectable({
    providedIn: 'root',
})
export class StorageService {
    private session: Storage;
    private local: Storage;

    private localChangesSubject = new Subject<{ key: string; data: unknown }>();
    private sessionChangesSubject = new Subject<{ key: string; data: unknown }>();

    constructor() {
        if (localStorage instanceof Storage && sessionStorage instanceof Storage) {
            this.session = sessionStorage;
            this.local = localStorage;
        } else {
            throw new ReferenceError('LocalStorage and/or SessionStorage have been modified and are no longer instances of Storage');
        }

        window.addEventListener('storage', (event: StorageEvent) => {
            try {
                if (event.storageArea === localStorage) {
                    this.localChangesSubject.next({ key: event.key as string, data: event.newValue ? JSON.parse(event.newValue) : null });
                } else if (event.storageArea === sessionStorage) {
                    this.sessionChangesSubject.next({ key: event.key as string, data: event.newValue ? JSON.parse(event.newValue) : null });
                }
            } catch (e) {
                if (!environment.production) {
                    console.error(e);
                }
            }
        });
    }

    public get localChanges() {
        return this.localChangesSubject.asObservable();
    }

    public get sessionChanges() {
        return this.sessionChangesSubject.asObservable();
    }

    private setStorage(storageType: StorageType, key: string, data: unknown): void {
        try {
            this[storageType].setItem(key, JSON.stringify(data));
        } catch (e) {
            if (!environment.production) {
                console.error(e);
            }
        }
    }

    private getStorage(storageType: StorageType, key: string): unknown {
        try {
            const data = this[storageType].getItem(key);

            if (data) {
                return JSON.parse(data as string);
            } else {
                return data;
            }
        } catch (e) {
            if (!environment.production) {
                console.error(e);
            }
            return undefined;
        }
    }

    private removeStorage(storageType: StorageType, key: string): void {
        try {
            this[storageType].removeItem(key);
        } catch (e) {
            if (!environment.production) {
                console.error(e);
            }
        }
    }

    public setInLocal(key: string, data: unknown): void {
        this.setStorage('local', key, data);
    }
    public getFromLocal(key: string): unknown {
        return this.getStorage('local', key);
    }
    public removeFromLocal(key: string): void {
        this.removeStorage('local', key);
    }

    public setInSession(key: string, data: unknown): void {
        this.setStorage('session', key, data);
    }
    public getFromSession(key: string): unknown {
        return this.getStorage('session', key);
    }
    public removeFromSession(key: string): void {
        this.removeStorage('session', key);
    }

    public clearSession(): void {
        this.session.clear();
    }
    public clearLocal(): void {
        this.local.clear();
    }
}
